import {
  css,
  styled,
  keyframes,
  SerializedStyles,
  forBrands,
} from "@core-ui/react-stitch";
import {getCheckmarkSVG} from "../utils";
import {BoxProps, BoxStyleFn} from "../types";

const getAnimationStyles: BoxStyleFn = ({
  theme,
  isChecked,
  crossBrand,
  isBRRedesignEnabled,
}) => {
  const backgroundColor = forBrands(
    theme,
    {
      br: (t) => (isBRRedesignEnabled ? t.color.b2 : t.color.b1),
      brfs: (t) => (isBRRedesignEnabled ? t.color.b2 : t.color.b1),
      gapfs: (t) => t.color.b1,
      gap: (t) => t.color.b1,
      on: (t) => t.color.b1,
      at: (t) => t.color.gray80,
      crossBrand: (t) => t.crossBrand.color.b1,
      default: (t) => t.color.bk,
    },
    crossBrand
  );

  const backgroundBounceKeyframes = keyframes`
    0% {
      transform: scale(0);
    }
    90% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  `;

  return css`
    &::before {
      content: "";
      display: block;
      position: absolute;
      width: calc(100% + 0.0625rem);
      height: calc(100% + 0.0625rem);
      transform: scale(${isChecked ? "1" : "0"});
      background-color: ${backgroundColor};
      ${isChecked &&
      css`
        animation: ${backgroundBounceKeyframes} 250ms
          cubic-bezier(0.175, 0.885, 0.32, 1.275);
      `}
    }
  `;
};

const getCheckmarkStyles: BoxStyleFn = ({theme, crossBrand, isChecked}) => {
  const getBackgroundPosition = (): SerializedStyles => {
    const x = "0";
    const y = "0.125rem";
    const checkedX = crossBrand ? "0.18755rem" : "0.125rem";
    const checkedY = crossBrand ? "0.3125rem" : "0.25rem";
    return css`
      background-position-x: ${isChecked ? checkedX : x};
      background-position-y: ${isChecked ? checkedY : y};
    `;
  };

  return css`
    &::after {
      content: "";
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      background-repeat: no-repeat;
      ${getBackgroundPosition()}
      background-size: ${isChecked ? "1.6875rem" : "1.875rem"};
      background-clip: border-box;
      background-color: transparent;
      ${isChecked &&
      css`
        background-image: ${getCheckmarkSVG({theme})};
      `}
    }
  `;
};

const getBorderStyle: BoxStyleFn = ({
  theme,
  crossBrand,
  hasError,
  isChecked,
  disabled,
  isBRRedesignEnabled,
}) => {
  const getBaseBorderColor = (): string => {
    if (crossBrand) return theme.crossBrand.color.g2;
    if (isBRRedesignEnabled) return theme.color.b2;
    return theme.color.gray60;
  };

  const checkedBorderColor = forBrands(
    theme,
    {
      br: (t) => (isBRRedesignEnabled ? t.color.b2 : t.color.b1),
      brfs: (t) => (isBRRedesignEnabled ? t.color.b2 : t.color.b1),
      gapfs: (t) => t.color.b1,
      gap: (t) => t.color.b1,
      on: (t) => t.color.b1,
      crossBrand: (t) => t.crossBrand.color.b1,
      default: (t) => t.color.bk,
    },
    crossBrand
  ) as string;

  const errorBorderColor = "transparent";
  const disabledBorderColor = theme.color.gray20;

  let borderColor: string;
  if (disabled) borderColor = disabledBorderColor;
  else if (isChecked) borderColor = checkedBorderColor;
  else if (hasError) borderColor = errorBorderColor;
  else borderColor = getBaseBorderColor();

  return css`
    border: 0.0625rem solid ${borderColor};
  `;
};

const getBaseStyles: BoxStyleFn = ({crossBrand, isBRRedesignEnabled}) =>
  css`
    box-sizing: ${crossBrand ? "content-box" : "border-box"};
    position: relative;
    display: block;
    margin-right: ${isBRRedesignEnabled ? "0.75rem" : "0.625rem"};
    min-width: 1.375rem;
    min-height: 1.375rem;
  `;

const getErrorStyles: BoxStyleFn = ({theme, hasError, isChecked}) =>
  !isChecked &&
  hasError &&
  css`
    box-shadow: 0 0 0 0.125rem ${theme.color.r1};
  `;

export const Box = styled.div<BoxProps>`
  ${getBaseStyles}
  ${getBorderStyle}
  ${getAnimationStyles}
  ${getCheckmarkStyles}
  ${getErrorStyles}
`;
